import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/Button'
import { WarnCard } from './ui/WarnCard'

/** The prototype's `tenantWideConfirm(message, onConfirm)`
 * (subscription-portal.html): a "Confirm change" modal whose body is one
 * amber `.warn-card` stating what the change does, with Cancel and a primary
 * Confirm — no checkbox gate, unlike AdminConfirmModal (MS-822; the User
 * Limits defaults Save). `note` is the portal's own line under the message
 * (the stale-write reload, OQ15 — no prototype equivalent).
 *
 * Like AdminConfirmModal, it waits for the server: while the write is in
 * flight (`pending`) Confirm, Cancel and the backdrop are inert. */
export function ConfirmChangeModal({
  subtitle,
  message,
  note,
  pending = false,
  onConfirm,
  onCancel,
}: Readonly<{
  /** The product the change is for — the page saves both products' drafts
   * with one confirm each, so the modal names which (ARCHITECTURE_DECISIONS.md
   * §46; the prototype's `.modal-header p`). */
  subtitle?: string
  message: ReactNode
  note?: string
  pending?: boolean
  onConfirm: () => void
  onCancel: () => void
}>) {
  const { t } = useTranslation()
  return (
    <div className="fixed inset-0 flex items-center justify-center z-[1000]">
      {/* The backdrop is a sibling of the dialog, not its parent, so a click
          inside the dialog never reaches it (AdminConfirmModal's pattern). It
          is a pointer convenience — keyboard and screen-reader users close
          with Cancel — hence hidden from both, and inert while in flight. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="confirm-change-backdrop"
        disabled={pending}
        className="absolute inset-0 bg-black/40 cursor-default"
        onClick={onCancel}
      />
      <dialog
        open
        aria-modal="true"
        aria-labelledby="confirm-change-title"
        className="relative m-0 p-0 border-0 text-inherit bg-[var(--bg-card)] rounded-xl shadow-[var(--shadow-lg)] w-[420px] max-w-[90vw] overflow-hidden"
      >
        <div className="px-6 pt-5 pb-4 border-b border-[var(--border-light)]">
          <h3 id="confirm-change-title" className="text-[16px] font-semibold text-[var(--text-primary)]">
            {t('userLimits.confirm.title')}
          </h3>
          {subtitle && <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5">{subtitle}</p>}
        </div>
        <div className="px-6 py-5">
          <WarnCard iconSize={16}>{message}</WarnCard>
          {note && (
            <p role="alert" className="mt-3 text-[12.5px] text-[var(--text-secondary)]">
              {note}
            </p>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[var(--border-light)] flex justify-end gap-2">
          <Button variant="ghost" disabled={pending} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" disabled={pending} onClick={onConfirm}>
            {t('common.confirm')}
          </Button>
        </div>
      </dialog>
    </div>
  )
}
